'use client';

import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useSwipe } from '@/lib/useSwipe';
import { useFocusTrap } from '@/lib/useFocusTrap';
import type { MediaItem } from '@/lib/media';

interface LightboxProps {
  items: MediaItem[];
  index: number;
  open: boolean;
  label: string;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}

function NavIcon({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d={dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SoundIcon({ muted }: { muted: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      {muted ? (
        <path d="M23 9l-6 6M17 9l6 6" />
      ) : (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M19 5a9 9 0 0 1 0 14" />
        </>
      )}
    </svg>
  );
}

// Directional slide: the incoming item enters from the side you're heading toward
// while the outgoing one slides out the opposite way (a gentle 64px offset + fade).
const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 64 : dir < 0 ? -64 : 0, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -64 : dir < 0 ? 64 : 0, opacity: 0 }),
};

/**
 * Full-screen media viewer rendered into document.body. Images and videos share
 * the same viewer and arrow/key/swipe navigation. A video opens paused (never
 * autoplays) with native play/pause controls, plus a mute toggle. Closes on
 * Escape, close button, or backdrop click. Body scroll is locked while open.
 */
export default function Lightbox({ items, index, open, label, onClose, onIndexChange }: LightboxProps) {
  const count = items.length;
  const current = items[index];
  const [mounted, setMounted] = useState(false);
  const [direction, setDirection] = useState(0);
  const [muted, setMuted] = useState(false);
  // Tracks which slide's media has loaded, so it fades in (reset implicitly when
  // index changes since loadedIndex no longer equals index).
  const [loadedIndex, setLoadedIndex] = useState(-1);
  const mediaShown = loadedIndex === index;
  const videoRef = useRef<HTMLVideoElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useFocusTrap(open, dialogRef);

  const go = (delta: number) => {
    setDirection(delta);
    onIndexChange((index + delta + count) % count);
  };
  const swipe = useSwipe(() => go(1), () => go(-1));

  useEffect(() => setMounted(true), []);

  // Keep the active video's mute state in sync (set imperatively so it applies
  // reliably as slides change).
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, index]);

  // Opening the viewer on a video (or navigating to one) starts playback — the
  // user already pressed play on the thumbnail, so no second click is needed.
  // Thumbnails themselves never autoplay; this only happens inside the viewer.
  useEffect(() => {
    if (!open || current?.type !== 'video') return;
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {
      // Sound-on autoplay can be blocked — fall back to muted playback.
      video.muted = true;
      setMuted(true);
      video.play().catch(() => {});
    });
  }, [open, index, current?.type]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') {
        setDirection(1);
        onIndexChange((index + 1) % count);
      }
      if (e.key === 'ArrowLeft') {
        setDirection(-1);
        onIndexChange((index - 1 + count) % count);
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, index, count, onClose, onIndexChange]);

  if (!mounted || typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={dialogRef}
          tabIndex={-1}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 outline-none sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={label}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
          onTouchStart={swipe.onTouchStart}
          onTouchEnd={swipe.onTouchEnd}
        >
          {/* Backdrop — click to close */}
          <div className="absolute inset-0 bg-background/90 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

          {/* Mute toggle (video only) */}
          {current?.type === 'video' && (
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="absolute right-16 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-on-surface/30 bg-surface/60 text-on-surface backdrop-blur-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <SoundIcon muted={muted} />
            </button>
          )}

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-on-surface/30 bg-surface/60 text-on-surface backdrop-blur-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>

          {/* Media — each slide is absolutely centered so the outgoing and incoming
              items overlap and animate simultaneously (no flicker). Changing index
              unmounts the previous slide, so a playing video stops automatically. */}
          <div className="relative z-[1] flex h-full w-full items-center justify-center overflow-hidden">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={index}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 360, damping: 36 },
                  opacity: { duration: 0.14 },
                }}
                className="absolute inset-0 flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Loading indicator until this slide's media is ready — covers the
                    empty gap when navigating to a not-yet-loaded video/image. */}
                {!mediaShown && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
                    <svg className="h-10 w-10 animate-spin text-on-surface/70" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4z" />
                    </svg>
                  </div>
                )}
                {current && (current.type === 'image' ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={encodeURI(current.src)}
                    alt={`${label} ${index + 1}`}
                    draggable={false}
                    onLoad={() => setLoadedIndex(index)}
                    className={clsx(
                      'max-h-[85vh] w-auto max-w-full select-none rounded-sm object-contain transition-opacity duration-500',
                      mediaShown ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                ) : (
                  <video
                    ref={videoRef}
                    src={encodeURI(current.src)}
                    controls
                    playsInline
                    preload="metadata"
                    aria-label={`${label} ${index + 1}`}
                    onLoadedData={() => setLoadedIndex(index)}
                    className={clsx(
                      'max-h-[85vh] w-auto max-w-full rounded-sm bg-black object-contain transition-opacity duration-500',
                      mediaShown ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous"
                className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-on-surface/30 bg-surface/60 text-on-surface backdrop-blur-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <NavIcon dir="left" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next"
                className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-on-surface/30 bg-surface/60 text-on-surface backdrop-blur-md transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <NavIcon dir="right" />
              </button>

              <span className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full bg-surface/70 px-4 py-1.5 text-label-caps font-headline tracking-widest text-on-surface backdrop-blur-md">
                {index + 1} / {count}
              </span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
